import React, { useEffect, useRef, useState } from "react";
import { ColumnValueRendererProps } from "../table.data.interface";
import { MondayApiQueryInstance } from "../../../services/monday/monday-service-ts";
import { getFileIcon } from "../../../utils/getFileIcon";
import { Tooltip } from "@vibe/core";
import useBoardAndItems from "../../../hooks/context/useBoardAndItems";
import logger from "../../../lib/logger";

interface FileColumValueInterface {
  name: string;
  file_extension: string;
  url: string;
  url_thumbnail: string;
  id: number;
  fileType?: string;
  linkToFile?: string;
}

const FilesColumn = ({
  columnData,
  itemIds,
  isPreview,
  otherBoardsValues,
}: ColumnValueRendererProps) => {
  const { currentBoard } = useBoardAndItems();
  const [fileColumnValue, setFileColumnValue] = useState<FileColumValueInterface[] | null>(null);
  const [showMore, setShowMore] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchStatusColumnData = async (itemIds: number[]) => {
      try {
        const response = await MondayApiQueryInstance.getFileColumnData(
          itemIds as number[],
          columnData.columnId,
        );

        if (response) {
          // Parse the column value to get LINK type files
          const columnValue = JSON.parse(response.items[0]?.column_values[0]?.value);
          const linkFiles = columnValue?.files
            ? columnValue.files
                .filter((file: any) => file.fileType === "LINK")
                .map((file: any) => ({
                  name: file.name,
                  file_extension: ".link",
                  url: file.linkToFile,
                  url_thumbnail: "",
                  id: file.fileId,
                }))
            : [];

          // Combine asset files with link files
          const allFiles = [...(response.items[0]?.assets || []), ...linkFiles];
          setFileColumnValue(allFiles);
        }
      } catch (error: any) {
        logger.error("Error fetching files column data", error);
        throw error;
      }
    };

    if (itemIds && itemIds.length > 0 && !isPreview) {
      fetchStatusColumnData(itemIds as number[]);
    } else if (isPreview) {
      const columnValue = JSON.parse(columnData?.value as string);

      const files = columnValue?.files
        ? columnValue.files.map((file: any) => ({
            name: file.name,
            file_extension:
              file.fileType === "LINK" ? ".link" : file.name.substring(file.name.lastIndexOf(".")),
            url: file.text,
            url_thumbnail: null,
            id: file.assetId,
          }))
        : [];

      setFileColumnValue(files);
    }
  }, [itemIds, columnData.columnId, columnData?.value, isPreview]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowMore(false);
      }
    };

    if (showMore) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMore]);

  if (columnData.value === "Empty Column" || fileColumnValue === null) {
    return null;
  }

  const handleShowMore = () => setShowMore(!showMore);

  const handleFileOpenDialog = (assetId: number, fileExtension: string, url: string) => {
    if (isPreview) return;

    try {
      if (fileExtension === ".link") {
        MondayApiQueryInstance.openLinkInTab(url);
      } else {
        MondayApiQueryInstance.openFilesDialog(
          currentBoard as number,
          itemIds![0] as number,
          columnData.columnId,
          assetId,
        );
      }
    } catch (error: any) {
      logger.error("Error opening files dialog", error);
    }
  };

  const displayedItems = fileColumnValue.slice(0, 3);
  const remainingFiles = fileColumnValue.slice(3);

  return (
    <div className={`flex relative ${isPreview ? "gap-0" : "gap-2"} items-center`}>
      {fileColumnValue &&
        displayedItems.map((file) => (
          <div
            key={file.id}
            className={`flex items-center ${isPreview ? "cursor-default" : "cursor-pointer"}`}
            onClick={() => {
              handleFileOpenDialog(file.id, file.file_extension, file.url);
            }}
          >
            <Tooltip content={file.name}>
              <div className="flex items-center gap-1 text-sm no-underline text-[#323338]">
                <div className="flex overflow-hidden items-center rounded">
                  {file.url_thumbnail ? (
                    <img
                      alt={file.name}
                      src={file.url_thumbnail}
                      width={24}
                      height={24}
                      className={`rounded ${isPreview ? "cursor-default" : "cursor-pointer"}`}
                    />
                  ) : (
                    getFileIcon(file.file_extension)
                  )}
                </div>

                {!isPreview && (
                  <span className="overflow-hidden whitespace-nowrap text-ellipsis max-w-[10ch]">
                    {file.name}
                  </span>
                )}
              </div>
            </Tooltip>
          </div>
        ))}

      {fileColumnValue.length > 3 && (
        <div
          onClick={handleShowMore}
          className="-mx-1 px-2 rounded-full text-white bg-[#DCDFEC] flex items-center justify-center cursor-pointer"
        >
          <span className="!font-poppins text-xs text-[#676879]">+{remainingFiles.length}</span>
        </div>
      )}

      {showMore && (
        <div
          ref={dropdownRef}
          className="absolute z-10 flex flex-col flex-wrap gap-2 p-2 bg-white bottom-[30px] shadow-custom min-w-[300px] rounded"
        >
          {remainingFiles.map((file) => (
            <div
              key={file.id}
              className="flex items-center gap-1 text-sm no-underline text-[#323338] w-full"
              onClick={() => {
                handleFileOpenDialog(file.id, file.file_extension, file.url);
              }}
            >
              <div
                className={`flex items-center gap-1 p-2 rounded transition-all w-full ${isPreview ? "cursor-default" : "cursor-pointer hover:bg-[#f5f6f8]"}`}
              >
                <div className="flex overflow-hidden items-center rounded">
                  {file.url_thumbnail ? (
                    <img
                      alt={file.name}
                      src={file.url_thumbnail}
                      width={24}
                      height={24}
                      className={`rounded ${isPreview ? "cursor-default" : "cursor-pointer"}`}
                    />
                  ) : (
                    getFileIcon(file.file_extension)
                  )}
                </div>
                <span className="overflow-hidden whitespace-nowrap text-ellipsis">{file.name}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FilesColumn;
